import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Clock, Package, Truck, CheckCircle, User, Navigation } from 'lucide-react';

import { getNewEvents, showDeliveryToast, statusKey } from '../utils/deliveryStatus';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5163';

const DeliveryTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastSeenRef = useRef(null); // ultima firma status|delivery_status vista

  // Cargar datos de tracking
  const fetchTracking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mobile/orders/${orderId}/realtime-tracking`);
      if (response.ok) {
        const data = await response.json();
        setTracking(data);
        updateMap(data);
        // Notificaciones in-app: la primera carga siembra en silencio (quien abre
        // el tracking de un pedido ya entregado no recibe nada) y luego se tostea
        // cada transicion detectada por el polling de 5 s
        const key = statusKey(data);
        if (lastSeenRef.current === null) {
          lastSeenRef.current = key;
        } else if (lastSeenRef.current !== key) {
          const event = getNewEvents(data, lastSeenRef.current);
          lastSeenRef.current = key;
          if (event) showDeliveryToast(event, data);
        }
      }
    } catch (error) {
      console.error('Error fetching tracking:', error);
    } finally {
      setLoading(false);
    }
  };

  // Inicializar mapa
  const initMap = async () => {
    try {
      // Por ahora usamos un mapa simple, luego se puede agregar Google Maps API
      // await loadGoogleMapsScript('YOUR_GOOGLE_MAPS_API_KEY');
      console.info('Map initialized');
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  // Actualizar marcadores en el mapa
  const updateMap = (data) => {
    if (!data.delivery_location) return;
    
    // Aquí iría la lógica de actualización de Google Maps
    // Por ahora solo guardamos los datos
    console.info('Updating map with location:', data.delivery_location);
  };

  useEffect(() => {
    initMap();
    fetchTracking();
    
    // Polling cada 5 segundos para actualizaciones en tiempo real
    const interval = setInterval(fetchTracking, 5000);
    
    return () => clearInterval(interval);
  }, [orderId]);

  const getStatusIcon = (status, deliveryStatus) => {
    if (deliveryStatus === 'delivered' || status === 'delivered') {
      return <CheckCircle className="w-6 h-6 text-green-600" />;
    }
    if (deliveryStatus === 'on_the_way' || deliveryStatus === 'nearby' || deliveryStatus === 'arrived') {
      return <Truck className="w-6 h-6 text-blue-600" />;
    }
    if (status === 'ready') {
      return <Package className="w-6 h-6 text-orange-600" />;
    }
    return <Clock className="w-6 h-6 text-gray-600" />;
  };

  const getStatusText = (status, deliveryStatus) => {
    if (deliveryStatus === 'delivered' || status === 'delivered') return 'Entregado';
    if (deliveryStatus === 'arrived') return 'Repartidor ha llegado';
    if (deliveryStatus === 'nearby') return 'Repartidor cerca';
    if (deliveryStatus === 'on_the_way') return 'En camino';
    if (deliveryStatus === 'assigned') return 'Repartidor asignado';
    if (status === 'ready') return 'Listo para entregar';
    if (status === 'in_preparation') return 'Preparando tu pedido';
    return 'Pedido recibido';
  };

  const getStatusColor = (status, deliveryStatus) => {
    if (deliveryStatus === 'delivered' || status === 'delivered') return 'bg-green-100 text-green-800';
    if (deliveryStatus === 'on_the_way' || deliveryStatus === 'nearby' || deliveryStatus === 'arrived') return 'bg-blue-100 text-blue-800';
    if (status === 'ready') return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando tracking...</p>
        </div>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-6 max-w-md">
          <p className="text-center text-gray-600">No se encontró información de tracking</p>
          <Button onClick={() => navigate('/')} className="w-full mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Seguimiento de Pedido</h1>
            <p className="text-sm text-gray-600">Orden #{tracking.order_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Estado Principal */}
        <Card className="p-6">
          <div className="flex items-center gap-4">
            {getStatusIcon(tracking.status, tracking.delivery_status)}
            <div className="flex-1">
              <h2 className="text-xl font-bold">
                {getStatusText(tracking.status, tracking.delivery_status)}
              </h2>
              <Badge className={`mt-2 ${getStatusColor(tracking.status, tracking.delivery_status)}`}>
                {tracking.delivery_status || tracking.status}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Mapa de Ubicación */}
        {tracking.delivery_location && tracking.delivery_status !== 'delivered' && (
          <Card className="overflow-hidden">
            <div className="w-full h-80 bg-gray-100 relative">
              {/* Mapa con OpenStreetMap (no requiere API Key) */}
              <div className="relative w-full h-full">
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${tracking.delivery_location.longitude - 0.01},${tracking.delivery_location.latitude - 0.01},${tracking.delivery_location.longitude + 0.01},${tracking.delivery_location.latitude + 0.01}&layer=mapnik&marker=${tracking.delivery_location.latitude},${tracking.delivery_location.longitude}`}
                  style={{ border: 0 }}
                  title="Mapa de ubicación"
                />
                        
                {/* Marcador de moto superpuesto */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none">
                  <div className="relative">
                    <div className="text-5xl animate-bounce" style={{ textShadow: '0 4px 8px rgba(0,0,0,0.3)' }}>
                      🏍️
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-black/30 rounded-full blur-sm"></div>
                  </div>
                </div>
              </div>
              {/* Overlay con info */}
              <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-blue-600 animate-pulse" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      Ubicación del repartidor
                    </p>
                    <p className="text-xs text-gray-500">
                      Actualizado: {new Date(tracking.delivery_location.timestamp || tracking.updated_at).toLocaleTimeString('es-ES')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 bg-gray-50 border-t">
              <a 
                href={`https://www.google.com/maps?q=${tracking.delivery_location.latitude},${tracking.delivery_location.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <MapPin className="w-4 h-4" />
                Abrir en Google Maps
              </a>
            </div>
          </Card>
        )}

        {/* Información del Repartidor */}
        {tracking.delivery_person && (
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Tu Repartidor
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Nombre:</span>
                <span className="font-medium">{tracking.delivery_person.name}</span>
              </div>
              {tracking.delivery_person.phone && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Teléfono:</span>
                  <a 
                    href={`tel:${tracking.delivery_person.phone}`}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <Phone className="w-4 h-4" />
                    {tracking.delivery_person.phone}
                  </a>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Dirección de Entrega */}
        {tracking.delivery_address && (
          <Card className="p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Dirección de Entrega
            </h3>
            <p className="text-gray-700">{tracking.delivery_address}</p>
          </Card>
        )}

        {/* Timeline de Estados */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Progreso del Pedido</h3>
          <div className="space-y-4">
            <TimelineItem 
              icon={<CheckCircle className="w-5 h-5" />}
              title="Pedido recibido"
              completed={true}
              active={tracking.status === 'pending'}
            />
            <TimelineItem 
              icon={<Package className="w-5 h-5" />}
              title="En preparación"
              completed={['in_preparation', 'ready', 'delivered'].includes(tracking.status)}
              active={tracking.status === 'in_preparation'}
            />
            <TimelineItem 
              icon={<Package className="w-5 h-5" />}
              title="Listo para entregar"
              completed={['ready', 'delivered'].includes(tracking.status) || tracking.delivery_status}
              active={tracking.status === 'ready' && !tracking.delivery_status}
            />
            <TimelineItem 
              icon={<Truck className="w-5 h-5" />}
              title="En camino"
              completed={['on_the_way', 'nearby', 'arrived', 'delivered'].includes(tracking.delivery_status)}
              active={tracking.delivery_status === 'on_the_way'}
            />
            <TimelineItem 
              icon={<MapPin className="w-5 h-5" />}
              title="Repartidor cerca"
              completed={['nearby', 'arrived', 'delivered'].includes(tracking.delivery_status)}
              active={tracking.delivery_status === 'nearby'}
            />
            <TimelineItem 
              icon={<CheckCircle className="w-5 h-5" />}
              title="Repartidor ha llegado"
              completed={['arrived', 'delivered'].includes(tracking.delivery_status)}
              active={tracking.delivery_status === 'arrived'}
            />
            <TimelineItem 
              icon={<CheckCircle className="w-5 h-5" />}
              title="Entregado"
              completed={tracking.status === 'delivered' || tracking.delivery_status === 'delivered'}
              active={false}
            />
          </div>
        </Card>

        {/* Botón de Actualizar */}
        <Button 
          onClick={fetchTracking} 
          className="w-full"
          variant="outline"
        >
          Actualizar Estado
        </Button>
      </div>
    </div>
  );
};

// Componente Timeline Item
const TimelineItem = ({ icon, title, completed, active }) => {
  return (
    <div className="flex items-start gap-3">
      <div className={`
        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
        ${completed ? 'bg-green-100 text-green-600' : 
          active ? 'bg-blue-100 text-blue-600' : 
          'bg-gray-100 text-gray-400'}
      `}>
        {icon}
      </div>
      <div className="flex-1 pt-2">
        <p className={`font-medium ${
          completed ? 'text-green-700' : 
          active ? 'text-blue-700' : 
          'text-gray-400'
        }`}>
          {title}
        </p>
        {active && (
          <p className="text-sm text-gray-500 mt-1">En progreso...</p>
        )}
        {completed && !active && (
          <p className="text-sm text-green-600 mt-1">✓ Completado</p>
        )}
      </div>
    </div>
  );
};

export default DeliveryTracking;
